import { styled } from '@zeiss/beyond-online-react';
import { EmptyState, WithNavigation } from '@zeiss/lib-components';
import { logError } from '@zeiss/lib-helpers';
import { RootBoundary as LibRootBoundary } from '@zeiss/lib-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import {
  isRouteErrorResponse,
  useNavigate,
  useRouteError,
} from 'react-router-dom';

import { AccountMenu, Footer } from 'src/components/layout';
import { PRODUCT_TITLE } from 'src/constants';
import { useGlobalEvaluations, useNavigationGroups } from 'src/hooks';

const CenteredContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 70vh;
`;

export const RootBoundary = () => {
  const { t } = useTranslation(['common']);

  const error = useRouteError();
  const navigate = useNavigate();
  const navigationGroups = useNavigationGroups();

  const { isAuthenticated, user } = useAuth();
  const { canUseApp, isLoading } = useGlobalEvaluations();

  useEffect(() => {
    if (isRouteErrorResponse(error) || error instanceof Response) return;

    logError(error, {
      customErrorType: 'React Router RootBoundary error',
    });
  }, [error]);

  const isAuthorizedForApp = isAuthenticated && !isLoading && canUseApp;

  /**
   * Custom 403
   *
   * Only displayed for users who are authorized for the app, but lack a
   * specific action they can do. This way we can offer a "Request access"
   * screen within the regular app layout.
   *
   * Note: `isAuthorizedForApp` is always true here in practice — `ProtectedRoute`
   * throws 404 (not 403) when `canUseApp` is false, so a 403 can only be
   * thrown after `APP_ACCESS` has already been confirmed as granted.
   */
  if (isAuthorizedForApp && error instanceof Response && error.status === 403) {
    const goHome = () => navigate('/');

    return (
      <WithNavigation
        accountMenu={<AccountMenu />}
        applicationTitle={PRODUCT_TITLE}
        footer={<Footer />}
        navigationGroups={navigationGroups}
        onLogoClick={goHome}
      >
        <CenteredContainer>
          <EmptyState
            align="center"
            description={t('common:noAccess.text', {
              email: user?.profile.email ?? '',
            })}
            headline={t('common:noAccess.title')}
            illustrationConfig={{
              img: 'Error',
            }}
            size="l"
          />
        </CenteredContainer>
      </WithNavigation>
    );
  }

  return (
    <LibRootBoundary
      footer={<Footer />}
      generic={isAuthorizedForApp ? false : true}
      productTitle={PRODUCT_TITLE}
      t={t}
    />
  );
};
